import { logger } from '#app/services/logger.js';
import {
  type CacheConfig,
  Environment,
  type GraphQLResponse,
  Network,
  QueryResponseCache,
  RecordSource,
  type RequestParameters,
  Store,
  type Variables,
} from '@contra/relay';
import { safeStringify } from '@contra/utilities/safeStringify';

const log = logger.child({
  namespace: 'RelayEnvironment',
});

export const createRelayEnvironment = ({
  apiUrl,
  relayRecordMap,
  releaseVersion,
}: {
  apiUrl: string;
  relayRecordMap?: ReturnType<InstanceType<typeof RecordSource>['toJSON']>;
  releaseVersion?: string;
}) => {
  const ONE_MINUTE = 60 * 1_000;
  const cache = new QueryResponseCache({ size: 250, ttl: ONE_MINUTE });

  const fetchQuery = async (
    operation: RequestParameters,
    variables: Variables,
    cacheConfig: CacheConfig,
  ): Promise<GraphQLResponse> => {
    /**
     * We're deep(ish)ly cloning variables here to ensure that, if any variables were
     * passed as a reference to a mutable object, the variables don't change between
     * retries (if a retry is called), for example, if we change route slugs that would
     * query different data from Relay while a mutation is still running and retries,
     * this adds an extra layer of protection to ensure that variables is not accidentally
     * mutated and submits data we don't want it to submit.
     *
     * See: https://contra-work.slack.com/archives/C01E15C2XE3/p1644001276097249
     */
    const clonedVariables = {
      ...variables,
      input:
        typeof variables['input'] === 'object'
          ? { ...variables['input'] }
          : undefined,
    };
    const queryId = operation.text ?? '';
    const isMutation = operation.operationKind === 'mutation';
    const isQuery = operation.operationKind === 'query';
    const forceFetch = cacheConfig?.force;

    log.debug(
      {
        operation: {
          id: operation.id,
          providedVariables: operation.providedVariables as {},
        },
      },
      'executing query %s',
      operation.name,
    );

    const fromCache = cache.get(queryId, clonedVariables);

    if (isQuery && fromCache !== null && !forceFetch) {
      return fromCache;
    }

    const headers: Record<string, string> = {
      accept: 'application/json',
      'content-type': 'application/json',
      // TODO provide at the time of initialization
      // eslint-disable-next-line no-restricted-properties
      'x-contra-client-url': window.location.href,
      'x-contra-posthog-session-id': window.posthogSessionId ?? '',
      'x-contra-posthog-user-id': window.posthogUserId ?? '',
      'x-contra-posthog-window-id': window.posthogWindowId ?? '',
      'x-contra-release-version': releaseVersion ?? '',
    };

    if (window.posthogSessionId) {
      headers['x-posthog-session-id'] = window.posthogSessionId;
    }

    const body = operation.text
      ? { query: operation.text, variables }
      : {
          doc_id: operation.id ?? undefined,
          operationName: operation.name,
          variables,
        };
    const response = await fetch(apiUrl, {
      body: safeStringify(body),
      credentials: 'include',
      headers,
      method: 'POST',
    });

    const json = await response.json();

    // We're bypassing Roarr here so that this shows up both in the Playwright trace viewer and in Posthog recordings
    if (json.errors) {
      // eslint-disable-next-line no-console
      console.error(
        safeStringify(
          {
            errors: json.errors,
            query: operation.text,
          },
          { space: 2 },
        ),
      );
    }

    if (isQuery && json && queryId !== '') {
      cache.set(queryId, variables, json);
    }

    if (isMutation) {
      cache.clear();
    }

    return json;
  };

  return new Environment({
    configName: 'server',
    network: Network.create(fetchQuery),
    store: new Store(
      new RecordSource(relayRecordMap),
      // Relay currently has a bug where released queries are not being re-fetched even
      // when the fetch policy is `network-only` or `store-and-network`. To work around this
      // we are disabling the GC buffer until a proper fix is released by Relay. - @erictaylor
      // See: https://contra-work.slack.com/archives/C026XPSKSRW/p1633648747066700?thread_ts=1633590513.043800&cid=C026XPSKSRW
      { gcReleaseBufferSize: 0 },
    ),
  });
};
